import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import type { ButtonSize, ButtonVariant } from '@/components/Button';
import { IconCheck, IconCopy } from '@/components/Icons';
import { copyToClipboard } from '@/lib/clipboard';

export interface CopyButtonProps {
  value: string;
  label?: string;
  copiedLabel?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

export function CopyButton({
  value,
  label = 'Copy',
  copiedLabel = 'Copied',
  variant = 'secondary',
  size = 'sm',
  className,
}: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const handleClick = useCallback(() => {
    void copyToClipboard(value).then((ok) => {
      setState(ok ? 'copied' : 'failed');
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setState('idle'), 2000);
    });
  }, [value]);

  const text = state === 'copied' ? copiedLabel : state === 'failed' ? 'Press Ctrl+C' : label;

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      className={className}
      icon={state === 'copied' ? <IconCheck className="h-3.5 w-3.5" /> : <IconCopy className="h-3.5 w-3.5" />}
    >
      {text}
      <span aria-live="polite" className="sr-only">
        {state === 'copied' ? 'Copied to clipboard' : ''}
      </span>
    </Button>
  );
}
